import { api, apiUpload } from '@techsofit/app-kit';
import type {
  CheckInResult,
  CoachOption,
  CreateGoalRequest,
  CreateGuestPassRequest,
  CreateMemberRequest,
  CreatePlanRequest,
  AlertsResponse,
  DashboardSummary,
  InsightsResponse,
  AttendanceLeaderboard,
  CheckInStreak,
  DayRegister,
  GuestPass,
  GymPaymentRow,
  GymPayment,
  StaffGuestPasses,
  Goal,
  GoalDetail,
  GoalDue,
  GymSettings,
  GymSubscription,
  InviteStaffRequest,
  CustomReminder,
  CustomReminderRequest,
  CreateCorporateClientRequest,
  CreateLeadRequest,
  CorporateClientRow,
  LeadRow,
  MemberDetail,
  MemberHealthReport,
  MemberSummary,
  UpdateCorporateClientRequest,
  UpdateLeadRequest,
  NotificationLogRow,
  MembershipPlan,
  MembershipReport,
  MembershipStatus,
  Page,
  Payment,
  PublicPricing,
  RecordCheckinRequest,
  RecordPaymentRequest,
  RevenueReport,
  BusinessHealth,
  TimeSeries,
  TrendMetric,
  StaffMember,
  Wrapped,
  WrappedShare,
  UpdateGoalRequest,
  UpdateGymSettingsRequest,
  UpdateMemberRequest,
  UpdatePlanRequest,
  UpdateStaffRequest,
  FinancePeriod,
  FinanceSummary,
  GymExpense,
  RecordExpenseRequest,
} from '@techsofit/api-types';

/** Typed calls against the gym API (/api/v1). All go through the cookie-auth client. */

export const getDashboard = () => api<DashboardSummary>('/dashboard');

/** Owner "needs attention" alerts (at-risk, expiring, birthdays). */
export const getAlerts = () => api<AlertsResponse>('/alerts');

/** Plain-language insight observations for the dashboard panel. */
export const getInsights = () => api<InsightsResponse>('/insights/panel');

export const getGymSettings = () => api<GymSettings>('/gym/settings');

// ── Guest passes (staff) ─────────────────────────────────────────────────────
export const getGuestPasses = () => api<StaffGuestPasses>('/gym/guest-passes');

export const createGuestPass = (req: CreateGuestPassRequest) =>
  api<GuestPass>('/gym/guest-passes', { method: 'POST', body: req });

/** Read-only: fetch a pass by code so the desk can verify the guest before checking in. */
export const lookupGuestPassByCode = (code: string) =>
  api<GuestPass>(`/gym/guest-passes/by-code/${encodeURIComponent(code)}`);

export const redeemGuestPassByCode = (code: string) =>
  api<GuestPass>(`/gym/guest-passes/redeem?code=${encodeURIComponent(code)}`, { method: 'POST' });

export const redeemGuestPass = (id: string) =>
  api<GuestPass>(`/gym/guest-passes/${id}/redeem`, { method: 'POST' });

export const cancelGuestPass = (id: string) =>
  api<void>(`/gym/guest-passes/${id}/cancel`, { method: 'POST' });

// ── Attendance / check-ins (staff) ────────────────────────────────────────────
export const getAttendance = (date?: string) =>
  api<DayRegister>(`/gym/attendance${date ? `?date=${date}` : ''}`);

export const checkInMember = (memberId: string, overrideAccess = false) =>
  api<CheckInResult>('/gym/attendance/check-in', {
    method: 'POST',
    body: { memberId, overrideAccess },
  });

export const getAttendanceLeaderboard = (period: 'WEEK' | 'MONTH' = 'WEEK') =>
  api<AttendanceLeaderboard>(`/gym/attendance/leaderboard?period=${period}`);

export const getMemberStreak = (memberId: string) =>
  api<CheckInStreak>(`/gym/attendance/streak?memberId=${memberId}`);

export const getSubscription = () => api<GymSubscription>('/gym/subscription');

/** This gym's TechSoFit subscription payment history (newest first). */
export const getSubscriptionPayments = () => api<GymPayment[]>('/gym/subscription/payments');

/** Public pricing (no auth) — used by the signup page to show the chosen plan. */
export const getPublicPricing = () => api<PublicPricing>('/public/subscription-plans');

export interface SubscriptionCheckout {
  reference: string;
  redirectUrl: string;
  status: string;
}

export interface SubscriptionPaymentStatus {
  reference: string;
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  message: string | null;
  redirectUrl: string | null;
}

/** Start a self-serve subscription payment (PesePay); returns the redirect URL to pay. */
export const startSubscriptionCheckout = (planId: string, billingCycle: 'MONTHLY' | 'ANNUAL') =>
  api<SubscriptionCheckout>('/gym/subscription/checkout', { method: 'POST', body: { planId, billingCycle } });

/** Poll a subscription payment's status after returning from PesePay. */
export const getSubscriptionPaymentStatus = (reference: string) =>
  api<SubscriptionPaymentStatus>(`/gym/subscription/payments/${reference}/status`);

export const updateGymSettings = (req: UpdateGymSettingsRequest) =>
  api<GymSettings>('/gym/settings', { method: 'PATCH', body: req });

export const getStaff = () => api<StaffMember[]>('/gym/staff');

export const inviteStaff = (req: InviteStaffRequest) =>
  api<StaffMember>('/gym/staff', { method: 'POST', body: req });

export const updateStaff = (id: string, req: UpdateStaffRequest) =>
  api<StaffMember>(`/gym/staff/${id}`, { method: 'PATCH', body: req });

export const resendStaffInvite = (id: string) =>
  api<StaffMember>(`/gym/staff/${id}/resend-invite`, { method: 'POST' });

export const disableStaff = (id: string) =>
  api<void>(`/gym/staff/${id}/disable`, { method: 'POST' });

/** Coaches of this gym (both roles) — feeds member assignment + PT payment pickers. */
export const getCoaches = () => api<CoachOption[]>('/gym/coaches');

/** A date range (inclusive), ISO `yyyy-MM-dd`. Both ends required together. */
export interface DateRange {
  from: string;
  to: string;
}

export const getRevenueReport = (
  opts: { months?: number; range?: DateRange; weekMonth?: string } = {},
) => {
  const qs = new URLSearchParams();
  if (opts.weekMonth) {
    qs.set('granularity', 'week');
    qs.set('month', opts.weekMonth); // "YYYY-MM"
  } else if (opts.range) {
    qs.set('from', opts.range.from);
    qs.set('to', opts.range.to);
  } else {
    qs.set('months', String(opts.months ?? 6));
  }
  return api<RevenueReport>(`/reports/revenue?${qs.toString()}`);
};

export const getMembershipReport = () => api<MembershipReport>('/reports/membership');

/** A bucketed metric over a trailing window (7/30/90/365 days) — powers the trends chart. */
export const getTimeseries = (metric: TrendMetric, days: number) =>
  api<TimeSeries>(`/reports/timeseries?metric=${metric}&days=${days}`);

/** Owner-level Business Health Score (0-100) with sub-scores. */
export const getBusinessHealth = () => api<BusinessHealth>('/insights/business-health');

/** Streams a CSV from the report export and triggers a browser download.
 * For payments, an optional [range] filters by paid-on date. */
export async function downloadReportCsv(
  type: 'members' | 'payments',
  range?: DateRange,
): Promise<void> {
  const qs = new URLSearchParams({ type });
  if (range) {
    qs.set('from', range.from);
    qs.set('to', range.to);
  }
  const res = await fetch(`/api/v1/reports/export?${qs.toString()}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${type}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const getPlans = () => api<MembershipPlan[]>('/plans');

export const createPlan = (req: CreatePlanRequest) =>
  api<MembershipPlan>('/plans', { method: 'POST', body: req });

export const updatePlan = (id: string, req: UpdatePlanRequest) =>
  api<MembershipPlan>(`/plans/${id}`, { method: 'PATCH', body: req });

export const deletePlan = (id: string) => api<void>(`/plans/${id}`, { method: 'DELETE' });

export interface MemberQuery {
  query?: string;
  status?: MembershipStatus;
  /** false lists deactivated members (the "Inactive" view); defaults to active. */
  active?: boolean;
  page?: number;
  size?: number;
}

export function listMembers(params: MemberQuery = {}) {
  const qs = new URLSearchParams();
  if (params.query) qs.set('query', params.query);
  if (params.status) qs.set('status', params.status);
  if (params.active != null) qs.set('active', String(params.active));
  if (params.page != null) qs.set('page', String(params.page));
  if (params.size != null) qs.set('size', String(params.size));
  const q = qs.toString();
  return api<Page<MemberSummary>>(`/members${q ? `?${q}` : ''}`);
}

export const getMember = (id: string) => api<MemberDetail>(`/members/${id}`);

/** Rules-based retention health for the whole active roster, worst-first. */
export const getMemberHealth = () => api<MemberHealthReport>('/members/health');

// ── Leads (CRM pipeline) ────────────────────────────────────────────────────

export const getLeads = () => api<LeadRow[]>('/leads');
export const createLead = (body: CreateLeadRequest) => api<LeadRow>('/leads', { method: 'POST', body });
export const updateLead = (id: string, body: UpdateLeadRequest) =>
  api<LeadRow>(`/leads/${id}`, { method: 'PATCH', body });
export const deleteLead = (id: string) => api<void>(`/leads/${id}`, { method: 'DELETE' });

// ── Corporate clients ───────────────────────────────────────────────────────

export const getCorporateClients = () => api<CorporateClientRow[]>('/corporate');
export const createCorporateClient = (body: CreateCorporateClientRequest) =>
  api<CorporateClientRow>('/corporate', { method: 'POST', body });
export const updateCorporateClient = (id: string, body: UpdateCorporateClientRequest) =>
  api<CorporateClientRow>(`/corporate/${id}`, { method: 'PATCH', body });
export const deleteCorporateClient = (id: string) => api<void>(`/corporate/${id}`, { method: 'DELETE' });

/** Assign or clear (null) the company sponsoring a member. */
export const setMemberCorporate = (id: string, corporateClientId: string | null) =>
  api<MemberDetail>(`/members/${id}/corporate`, { method: 'PUT', body: { corporateClientId } });

export const getMemberPayments = (id: string) => api<Payment[]>(`/members/${id}/payments`);

/** Every payment in the gym (voided and not), newest first, with member names. */
export const listAllPayments = () => api<GymPaymentRow[]>('/payments');

/** Reverse a payment — undoes its effect and recomputes the member's expiry/PT.
 * The record is kept (struck-through) for audit. */
export const voidPayment = (id: string, reason: string) =>
  api<Payment>(`/payments/${id}/void`, { method: 'POST', body: { reason } });

export const recordPayment = (req: RecordPaymentRequest) =>
  api<Payment>('/payments', { method: 'POST', body: req });

export const createMember = (req: CreateMemberRequest) =>
  api<MemberDetail>('/members', { method: 'POST', body: req });

export const updateMember = (id: string, req: UpdateMemberRequest) =>
  api<MemberDetail>(`/members/${id}`, { method: 'PATCH', body: req });

/** Assign (or clear, with coachId null) a member's coach. */
export const setMemberCoach = (id: string, coachId: string | null) =>
  api<MemberDetail>(`/members/${id}/coach`, { method: 'PUT', body: { coachId } });

export const deactivateMember = (id: string) =>
  api<void>(`/members/${id}/deactivate`, { method: 'POST' });

export const activateMember = (id: string) =>
  api<void>(`/members/${id}/activate`, { method: 'POST' });

/** Hard-delete a member and everything associated with them (owner-only).
 * Blocked server-side when they have non-voided payments. [confirm] must equal
 * the member's exact name. Irreversible. */
export const deleteMember = (id: string, confirm: string) =>
  api<void>(`/members/${id}?confirm=${encodeURIComponent(confirm)}`, { method: 'DELETE' });

// ── Goals ──────────────────────────────────────────────────────────────────
export const getMemberGoals = (memberId: string) => api<Goal[]>(`/members/${memberId}/goals`);

export const createGoal = (memberId: string, req: CreateGoalRequest) =>
  api<GoalDetail>(`/members/${memberId}/goals`, { method: 'POST', body: req });

export const getGoal = (id: string) => api<GoalDetail>(`/goals/${id}`);

export const updateGoal = (id: string, req: UpdateGoalRequest) =>
  api<GoalDetail>(`/goals/${id}`, { method: 'PATCH', body: req });

export const recordCheckin = (id: string, req: RecordCheckinRequest) =>
  api<GoalDetail>(`/goals/${id}/checkins`, { method: 'POST', body: req });

export const deleteCheckin = (id: string, checkinId: string) =>
  api<GoalDetail>(`/goals/${id}/checkins/${checkinId}`, { method: 'DELETE' });

export function uploadCheckinPhoto(id: string, checkinId: string, file: File) {
  const form = new FormData();
  form.append('file', file);
  return apiUpload<GoalDetail>(`/goals/${id}/checkins/${checkinId}/photo`, form);
}

export const getGoalsDue = () => api<GoalDue[]>('/goals/due');

export const snoozeGoal = (id: string, days: number) =>
  api<GoalDetail>(`/goals/${id}/snooze`, { method: 'POST', body: { days } });

export const getMemberWrapped = (memberId: string) => api<Wrapped>(`/members/${memberId}/wrapped`);

export const shareMemberWrapped = (memberId: string) =>
  api<WrappedShare>(`/members/${memberId}/wrapped/share`, { method: 'POST' });

// ── Workouts & exercise library ──────────────────────────────────────────────

import type {
  Exercise,
  ExerciseFilters,
  Workout,
  WorkoutRequest,
  WorkoutGroup,
  GroupRequest,
  WorkoutAssignment,
  AssignmentRequest,
  InlineWorkoutRequest,
  AssignmentCompletion,
  Compliance,
} from '@techsofit/api-types';

export interface ExerciseQuery {
  muscleGroup?: string;
  equipment?: string;
  category?: string;
  q?: string;
}

export function getExercises(p: ExerciseQuery = {}) {
  const qs = new URLSearchParams();
  if (p.muscleGroup) qs.set('muscleGroup', p.muscleGroup);
  if (p.equipment) qs.set('equipment', p.equipment);
  if (p.category) qs.set('category', p.category);
  if (p.q) qs.set('q', p.q);
  const s = qs.toString();
  return api<Exercise[]>(`/exercises${s ? `?${s}` : ''}`);
}

export const getExerciseFilters = () => api<ExerciseFilters>('/exercises/filters');

/** Add a gym's own custom exercise (visible only to this gym). */
export const createCustomExercise = (body: Record<string, unknown>) =>
  api<Exercise>('/exercises', { method: 'POST', body });

export const updateCustomExercise = (id: string, body: Record<string, unknown>) =>
  api<Exercise>(`/exercises/${id}`, { method: 'PATCH', body });

export const deleteCustomExercise = (id: string) =>
  api<void>(`/exercises/${id}`, { method: 'DELETE' });

/** Upload a gym image (e.g. a custom-exercise photo); returns the stored URL. */
export const uploadGymImage = (file: Blob, filename = 'image.jpg') => {
  const form = new FormData();
  form.append('file', file, filename);
  return apiUpload<{ url: string }>('/media/image', form);
};

export const getWorkouts = () => api<Workout[]>('/workouts');
export const getWorkout = (id: string) => api<Workout>(`/workouts/${id}`);
export const createWorkout = (req: WorkoutRequest) =>
  api<Workout>('/workouts', { method: 'POST', body: req });
export const updateWorkout = (id: string, req: WorkoutRequest) =>
  api<Workout>(`/workouts/${id}`, { method: 'PUT', body: req });
export const deleteWorkout = (id: string) => api<void>(`/workouts/${id}`, { method: 'DELETE' });

export const getWorkoutGroups = () => api<WorkoutGroup[]>('/groups');
export const createWorkoutGroup = (req: GroupRequest) =>
  api<WorkoutGroup>('/groups', { method: 'POST', body: req });
export const updateWorkoutGroup = (id: string, req: GroupRequest) =>
  api<WorkoutGroup>(`/groups/${id}`, { method: 'PUT', body: req });
export const deleteWorkoutGroup = (id: string) => api<void>(`/groups/${id}`, { method: 'DELETE' });

export const getBoard = (from: string, to: string) =>
  api<WorkoutAssignment[]>(`/workout-assignments?from=${from}&to=${to}`);
export const createAssignment = (req: AssignmentRequest) =>
  api<WorkoutAssignment>('/workout-assignments', { method: 'POST', body: req });

/** Build a workout on the fly and schedule it (member/group/everyone) in one step. */
export const createInlineAssignment = (req: InlineWorkoutRequest) =>
  api<WorkoutAssignment>('/workout-assignments/inline', { method: 'POST', body: req });

/** A member's scheduled workouts, newest first (member-page history + duplicate). */
export const getMemberWorkouts = (memberId: string) =>
  api<WorkoutAssignment[]>(`/workout-assignments/member/${memberId}`);
export const deleteAssignment = (id: string) =>
  api<void>(`/workout-assignments/${id}`, { method: 'DELETE' });

export const getAssignmentCompletions = (id: string) =>
  api<AssignmentCompletion[]>(`/workout-assignments/${id}/completions`);

export const markAssignmentCompletion = (id: string, memberId: string, completed: boolean) =>
  api<void>(`/workout-assignments/${id}/completions`, { method: 'POST', body: { memberId, completed } });

export const getCompliance = (from: string, to: string) =>
  api<Compliance[]>(`/workout-assignments/compliance?from=${from}&to=${to}`);

/** This gym's recently sent notifications (proof reminders are going out). */
export const getNotifications = (limit = 50) =>
  api<NotificationLogRow[]>(`/gym/notifications?limit=${limit}`);

// ── Custom reminders (owner-authored, max 3) ──────────────────────────────
export const getCustomReminders = () => api<CustomReminder[]>('/gym/reminders');
export const createCustomReminder = (req: CustomReminderRequest) =>
  api<CustomReminder>('/gym/reminders', { method: 'POST', body: req });
export const updateCustomReminder = (id: string, req: CustomReminderRequest) =>
  api<CustomReminder>(`/gym/reminders/${id}`, { method: 'PUT', body: req });
export const deleteCustomReminder = (id: string) =>
  api<void>(`/gym/reminders/${id}`, { method: 'DELETE' });

export const getFinanceSummary = (period: FinancePeriod) =>
  api<FinanceSummary>(`/finance/summary?period=${period}`);

export const listExpenses = (from: string, to: string) =>
  api<GymExpense[]>(`/finance/expenses?from=${from}&to=${to}`);

export const recordExpense = (req: RecordExpenseRequest) =>
  api<GymExpense>('/finance/expenses', { method: 'POST', body: req });

export const deleteExpense = (id: string) =>
  api<void>(`/finance/expenses/${id}`, { method: 'DELETE' });
